import { FeatureFlag } from '@ps/web-core-enums';
import type { TranslationTag } from '@ps/web-core-intl';
import { useFeature } from '@ps/web-core-store';
import { Translation } from '@ps/web-shared-components';
import { translatedLink } from '@ps/web-shared-ui';
import React from 'react';
import { useSelector } from 'react-redux';

import { Country } from '@swf/core/enums';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';

import { CardType } from '../../../../../../types/cardType';
import { getPaySystemName } from '../../../../../../utils/cashier';
import Alert from '../../../../../Alert/Alert';
import {
  creditTimeDays,
  creditTimeHours,
  creditTimeInstant,
  creditTimeMinutes,
  creditTimeTwoDays,
  customRedirectMethods,
  redirectMethods
} from '../../../../data';

import {
  TXTWCL_By_confirmingX_you_agree_that__ELL,
  TXTWCL_Confirm_Bank_details,
  TXTWCL_Confirm_Bank_transfer,
  TXTWCL_Confirm_voucher_amount,
  TXTWCL_Confirm_with_N0,
  TXTWCL_Confirm_with_Plaid,
  TXTWCL_Depending_on_your_bankXs_prefe_ELL,
  TXTWCL_Depending_on_your_preferencesX_ELL,
  TXTWCL_Please_check_the_amount_on_you_ELL,
  TXTWCL_Please_confirm_your_deposit_to_ELL,
  TXTWCL_Processor_fees_may_applyX,
  TXTWCL_This_can_take_up_to_24_hours_t_ELL,
  TXTWCL_This_can_take_up_to_4_5_days_t_ELL,
  TXTWCL_This_can_take_up_to_5_minutes__ELL,
  TXTWCL_To_complete_this_transactionX__ELL,
  TXTWCL_To_complete_this_transactionX__ELL_1,
  TXTWCL_You_will_be_redirected_to_N0_t_ELL,
  TXTWCL_You_will_be_redirected_to_the__ELL_2
} from './InfoMessages.messages.json';
import { container, info, title } from './InfoMessages.scss';

type RedirectMethodsCardTypes =
  | CardType.BankWire
  | CardType.MasterCard
  | CardType.MBWay
  | CardType.MuchBetter
  | CardType.OpenBanking
  | CardType.PsPayAstropay
  | CardType.PsPayInovapay
  | CardType.PStarsTopUp
  | CardType.Ricarica
  | CardType.Visa;

const getCustomRedirectMethodsText = (type: RedirectMethodsCardTypes) => {
  const text: Record<RedirectMethodsCardTypes, { title: TranslationTag; text: TranslationTag }> = {
    [CardType.BankWire]: {
      title: TXTWCL_Confirm_Bank_transfer,
      text: TXTWCL_To_complete_this_transactionX__ELL
    },
    [CardType.MasterCard]: {
      title: TXTWCL_Confirm_Bank_details,
      text: TXTWCL_Depending_on_your_bankXs_prefe_ELL
    },
    [CardType.MBWay]: {
      title: TXTWCL_Confirm_with_N0,
      text: TXTWCL_Please_confirm_your_deposit_to_ELL
    },
    [CardType.MuchBetter]: {
      title: TXTWCL_Confirm_with_N0,
      text: TXTWCL_To_complete_this_transactionX__ELL_1
    },
    [CardType.OpenBanking]: {
      title: TXTWCL_Confirm_with_Plaid,
      text: TXTWCL_By_confirmingX_you_agree_that__ELL
    },
    [CardType.PsPayAstropay]: {
      title: TXTWCL_Confirm_Bank_transfer,
      text: TXTWCL_You_will_be_redirected_to_the__ELL_2
    },
    [CardType.PsPayInovapay]: {
      title: TXTWCL_Confirm_Bank_transfer,
      text: TXTWCL_You_will_be_redirected_to_the__ELL_2
    },
    [CardType.PStarsTopUp]: {
      title: TXTWCL_Confirm_voucher_amount,
      text: TXTWCL_Please_check_the_amount_on_you_ELL
    },
    [CardType.Ricarica]: {
      title: TXTWCL_Confirm_voucher_amount,
      text: TXTWCL_Please_check_the_amount_on_you_ELL
    },
    [CardType.Visa]: {
      title: TXTWCL_Confirm_Bank_details,
      text: TXTWCL_Depending_on_your_bankXs_prefe_ELL
    }
  };
  return text[type];
};

const getRedirectInfo = (type: RedirectMethodsCardTypes) =>
  redirectMethods.includes(type)
    ? TXTWCL_You_will_be_redirected_to_N0_t_ELL
    : getCustomRedirectMethodsText(type).text;

const getRedirectTitle = (type: RedirectMethodsCardTypes) =>
  redirectMethods.includes(type)
    ? TXTWCL_Confirm_with_N0
    : getCustomRedirectMethodsText(type).title;

const getCreditTimeMessage = (type: CardType, country: Country) => {
  if (type === CardType.PsPayAstropay && country) {
    switch (country) {
      case Country.Peru:
      case Country.Uruguay:
        return <Translation {...TXTWCL_This_can_take_up_to_24_hours_t_ELL} values={{ 0: 24 }} />;
      case Country.Mexico:
      case Country.Brazil:
        return <Translation {...TXTWCL_This_can_take_up_to_24_hours_t_ELL} values={{ 0: 48 }} />;
      case Country.Chile:
        return <Translation {...TXTWCL_This_can_take_up_to_5_minutes__ELL} />;
    }
  }
  if ([...creditTimeMinutes, ...creditTimeInstant].includes(type)) {
    return <Translation {...TXTWCL_This_can_take_up_to_5_minutes__ELL} />;
  }
  if (creditTimeDays.includes(type)) {
    return <Translation {...TXTWCL_This_can_take_up_to_4_5_days_t_ELL} />;
  }
  if (creditTimeHours.includes(type)) {
    return <Translation {...TXTWCL_This_can_take_up_to_24_hours_t_ELL} values={{ 0: 24 }} />;
  }
  if (creditTimeTwoDays.includes(type)) {
    return <Translation {...TXTWCL_This_can_take_up_to_24_hours_t_ELL} values={{ 0: 48 }} />;
  }
};

const InfoMessages: React.FC = () => {
  const {
    data: {
      savedCardInfoId,
      paySystemDetails: { type },
      paySystems
    }
  } = useWizardContext();
  const country = useSelector(select.account.getCountry) as Country;
  const showCreditTime = useFeature(FeatureFlag.SwcCreditTime);
  const i18nSystemName = getPaySystemName(paySystems, type);

  return (
    <>
      {showCreditTime && (
        <div className={info}>
          {getCreditTimeMessage(type, country)}{' '}
          <Translation {...TXTWCL_Processor_fees_may_applyX} />
        </div>
      )}
      {redirectMethods.includes(type) || customRedirectMethods.includes(type) ? (
        <div className={container}>
          <Alert variant="info">
            <>
              <div className={title}>
                <Translation {...getRedirectTitle(type)} values={{ 0: i18nSystemName }} />
              </div>
              <div>
                {type === CardType.OpenBanking ? (
                  <Translation
                    {...getRedirectInfo(type)}
                    values={{
                      a: translatedLink({
                        target: '_blank',
                        rel: 'norefferrer',
                        href: 'https://plaid.com/legal/#end-user-privacy-policy'
                      })
                    }}
                  />
                ) : (type === CardType.PayPal || type === CardType.LuxonPay) && savedCardInfoId ? (
                  <Translation
                    {...TXTWCL_Depending_on_your_preferencesX_ELL}
                    values={{ 0: i18nSystemName }}
                  />
                ) : (
                  <Translation {...getRedirectInfo(type)} values={{ 0: i18nSystemName }} />
                )}
              </div>
            </>
          </Alert>
        </div>
      ) : null}
    </>
  );
};

export default InfoMessages;
